import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, alpha, useTheme,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Divider, Alert, Paper, TextField, MenuItem,
  FormControl, InputLabel, Select, Checkbox,
  Skeleton, Tooltip, InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import {
  PointOfSale, School, CalendarMonth, AutoFixHigh,
  Print, CheckCircle,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feePaymentService from '../../services/feePaymentService';
import {
  CollectibleResponse, CollectibleInvoice,
  FeePaymentMethod, PaidByKind,
  RecordFeePaymentResult,
} from '../../types/fees';
import { useAuth } from '../../context/AuthContext';
import ChallanNumberCell from './ChallanNumberCell';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { PAYMENT_METHOD_LABEL, PAYMENT_METHODS } from '../../utils/paymentMethods';
import { useReceipts } from '../finance/useReceipts';

interface Props {
  open: boolean;
  /** Caller passes one of personId / studentId. Admissions flow has
   *  personId directly; tuition cashier flow has studentId. Server resolves
   *  studentId → Person; dialog loads collectible by whichever was given. */
  personId?: string | null;
  studentId?: string | null;
  onClose: () => void;
  onRecorded: (result: RecordFeePaymentResult) => void;
  /** Open straight into taking an advance (nothing is owed). */
  advance?: boolean;
}

interface AllocationDraft {
  invoiceId: string;
  amount: string;    // string in the UI so users can clear a field
  selected: boolean;
}

const money = (n: number) => Math.round(n * 100) / 100;

/**
 * RecordFeePaymentDialog — the accountant's daily hot path.
 *
 * Flow
 * ----
 *   1. Dialog opens with a studentId. Loads collectible in one call:
 *      { student, totalOutstanding, invoices[] }
 *   2. Every outstanding invoice is pre-selected with amount = its
 *      outstanding. Admin can uncheck rows or edit amounts inline.
 *   3. Received amount at the top updates live from the allocation sum.
 *      "Collect all outstanding" one-click chip snaps to the full total.
 *   4. Method, reference, receivedAt, paidBy captured on the right.
 *   5. Save posts one transaction → receipt number + ledger entry.
 */
const RecordFeePaymentDialog: React.FC<Props> = ({
  open, personId, studentId, onClose, onRecorded, advance = false,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();

  const [loading, setLoading] = useState(false);
  const [collectible, setCollectible] = useState<CollectibleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<RecordFeePaymentResult | null>(null);
  // What the saved receipt covered — fixed at save time (the balances reload after).
  const [resultSummary, setResultSummary] = useState('');
  const { downloadFeeReceipt: downloadReceipt, preparingReceipt } = useReceipts();

  // Payment form fields
  const [amountInput, setAmountInput] = useState<string>('');
  const [method, setMethod] = useState<FeePaymentMethod>('cash');
  const [receivedAt, setReceivedAt] = useState<string>(() => format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paidByKind, setPaidByKind] = useState<PaidByKind>('parent');
  const [paidByName, setPaidByName] = useState('');
  const [notes, setNotes] = useState('');

  // Allocations
  const [allocations, setAllocations] = useState<AllocationDraft[]>([]);

  // Advances: spend what the family holds, keep an overpayment, or take an
  // advance when nothing is due.
  const [useAdvance, setUseAdvance] = useState(false);
  const [keepExtra, setKeepExtra] = useState(false);
  const [receivingAdvance, setReceivingAdvance] = useState(false);

  const currencyFmt = useCurrencyFormat();

  // ─── Load collectible when opened ────────────────────────────────────────

  const loadCollectible = useCallback(async () => {
    if (!personId && !studentId) return;
    setLoading(true);
    setError(null);
    try {
      // Prefer personId (admission flow); fall back to studentId (tuition
      // flow — server resolves the Student's linked Person once).
      const data = personId
        ? await feePaymentService.getCollectibleByPerson(personId)
        : await feePaymentService.getCollectible(studentId!);
      setCollectible(data);

      // Pre-fill allocations from the collectible invoices, oldest-first.
      const drafts: AllocationDraft[] = data.invoices.map(inv => ({
        invoiceId: inv._id,
        amount: String(inv.totals.outstanding),
        selected: true,
      }));
      setAllocations(drafts);
      setAmountInput(String(data.totalOutstanding));

      // Sensible paidBy default: father's name if we have it.
      if (data.student.fatherName) setPaidByName(data.student.fatherName);
      else setPaidByName(data.student.name);
    } catch (e: any) {
      setError(e?.message || 'Failed to load outstanding challans');
    } finally {
      setLoading(false);
    }
  }, [personId, studentId]);

  useEffect(() => {
    if (!open || (!personId && !studentId)) {
      setCollectible(null);
      setAllocations([]);
      setError(null);
      setResult(null);
      return;
    }
    loadCollectible();
    setMethod('cash');
    setReferenceNumber('');
    setReceivedAt(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setPaidByKind('parent');
    setNotes('');
    setUseAdvance(false);
    setKeepExtra(false);
    setReceivingAdvance(advance);
    if (advance) setAmountInput('');
  }, [open, personId, studentId, loadCollectible, advance]);

  // ─── Derived totals ──────────────────────────────────────────────────────

  const allocatedTotal = useMemo(() => {
    let s = 0;
    for (const a of allocations) {
      if (!a.selected) continue;
      const v = Number(a.amount) || 0;
      if (v > 0) s += v;
    }
    return money(s);
  }, [allocations]);

  const amount = Number(amountInput) || 0;
  const advanceOnHand = collectible?.advanceBalance ?? 0;
  const available = useAdvance ? advanceOnHand : 0;
  // received now + paid from advance = allocated + kept as advance
  const fromAdvance = useAdvance ? money(Math.max(0, allocatedTotal - amount)) : 0;
  const extra = money(amount - allocatedTotal);
  const advanceAmount = !useAdvance && (keepExtra || receivingAdvance) && extra > 0 ? extra : 0;
  const isAllocatedMatch = useAdvance
    ? fromAdvance <= available + 0.005 && amount <= allocatedTotal + 0.005
    : Math.abs(allocatedTotal + advanceAmount - amount) <= 0.005;
  const overAllocated = allocatedTotal > amount + available + 0.005;

  const perInvoice = useMemo(() => new Map(
    (collectible?.invoices ?? []).map(i => [i._id, i]),
  ), [collectible]);

  const nonEmptyAllocations = allocations.filter(a => a.selected && (Number(a.amount) || 0) > 0);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const setAllocationField = (invoiceId: string, patch: Partial<AllocationDraft>) => {
    setAllocations(prev => prev.map(a => a.invoiceId === invoiceId ? { ...a, ...patch } : a));
  };

  const collectAll = () => {
    if (!collectible) return;
    setAllocations(collectible.invoices.map(inv => ({
      invoiceId: inv._id,
      amount: String(inv.totals.outstanding),
      selected: true,
    })));
    setAmountInput(String(collectible.totalOutstanding));
  };

  const redistribute = (targetAmount: number) => {
    // Fair distribution across selected invoices, oldest-first, capped at each
    // invoice's outstanding. Any leftover leaves later rows at 0.
    if (!collectible) return;
    let remaining = money(targetAmount);
    const next = allocations.map(a => ({ ...a }));
    for (const draft of next) {
      if (!draft.selected) { draft.amount = '0'; continue; }
      const inv = perInvoice.get(draft.invoiceId);
      const outstanding = inv?.totals.outstanding ?? 0;
      const give = money(Math.min(remaining, outstanding));
      draft.amount = String(give);
      remaining = money(remaining - give);
    }
    setAllocations(next);
  };

  const handleAmountChange = (v: string) => {
    setAmountInput(v);
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) {
      redistribute(n + available);
    }
  };

  /** Spend the advance first; the cash still needed becomes the received amount. */
  const toggleAdvance = (on: boolean) => {
    setUseAdvance(on);
    setKeepExtra(false);
    setAmountInput(String(on ? money(Math.max(0, allocatedTotal - advanceOnHand)) : allocatedTotal));
  };

  const save = async () => {
    // Need collectible loaded AND at least one payer identifier. Admission
    // flow passes personId (no studentId); tuition flow passes studentId.
    if (!collectible || (!personId && !studentId)) return;
    if (!isAllocatedMatch) {
      enqueueSnackbar(
        overAllocated
          ? 'Allocated total exceeds what is available — reduce line amounts.'
          : 'Allocate the rest to a challan, or keep it as an advance.',
        { variant: 'warning' },
      );
      return;
    }
    if (nonEmptyAllocations.length === 0 && !advanceAmount) {
      enqueueSnackbar('Pick at least one challan to allocate the payment to.', { variant: 'warning' });
      return;
    }
    if (!paidByName.trim()) {
      enqueueSnackbar('Paid-by name is required.', { variant: 'warning' });
      return;
    }
    if ((method === 'cheque' || method === 'bank_transfer') && !referenceNumber.trim()) {
      enqueueSnackbar(`${PAYMENT_METHOD_LABEL[method]} requires a reference number.`, { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const res = await feePaymentService.recordFeePayment({
        // Pass whichever we have — server resolves to Person either way.
        personId: personId ?? undefined,
        studentId: studentId ?? undefined,
        amount: money(amount),
        advanceAmount,
        fromAdvance,
        method,
        receivedAt: new Date(receivedAt).toISOString(),
        paidBy: { kind: paidByKind, name: paidByName.trim() },
        allocations: nonEmptyAllocations.map(a => ({
          invoiceId: a.invoiceId,
          amount: money(Number(a.amount) || 0),
        })),
        referenceNumber: referenceNumber.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setResultSummary(nonEmptyAllocations.length === 0 ? `Received ${currencyFmt(money(amount))} as advance.` : [
        `Recorded ${currencyFmt(money(amount))}`,
        nonEmptyAllocations.length > 0 && ` against ${nonEmptyAllocations.length} challan${nonEmptyAllocations.length === 1 ? '' : 's'}`,
        fromAdvance > 0 && `, ${currencyFmt(fromAdvance)} from advance`,
        advanceAmount > 0 && `, ${currencyFmt(advanceAmount)} kept as advance`,
      ].filter(Boolean).join('') + '.');
      setResult(res);
      onRecorded(res);
      await loadCollectible();
      enqueueSnackbar(`Receipt ${res.receiptNumber} saved`, { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to record payment', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  const invoiceHint = (inv: CollectibleInvoice) => {
    const isOverdue = new Date(inv.dueDate) < new Date();
    if (isOverdue) return { label: 'Overdue', color: theme.palette.error };
    if (inv.status === 'partial') return { label: 'Partial', color: theme.palette.warning };
    return null;
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <PointOfSale color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            {result ? `Receipt ${result.receiptNumber}` : receivingAdvance ? 'Receive advance' : 'Record payment'}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {result
            ? 'Payment recorded and posted to the ledger.'
            : receivingAdvance
              ? 'Money received before a challan exists. It is held for this family and can pay their next challans.'
              : 'Allocate what is received to challans. Anything extra can be kept as an advance.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={70} />
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={80} />
          </Stack>
        )}

        {error && !loading && (
          <Alert severity="error" variant="outlined">{error}</Alert>
        )}

        {!loading && collectible && (
          <>
            {/* Student card */}
            <Box sx={{
              p: 2, borderRadius: 2, mb: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body1" fontWeight={700}>{collectible.student.name}</Typography>
                    {collectible.student.grNumber && (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
                        {collectible.student.grNumber}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <School fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    {collectible.student.applicationNumber
                      ? `Applicant · ${collectible.student.applicationNumber}`
                      : collectible.student.class?.name ?? '—'}
                    {collectible.student.section?.name && ` – ${collectible.student.section.name}`}
                    {collectible.student.rollNumber != null && ` · Roll ${collectible.student.rollNumber}`}
                  </Typography>
                  {collectible.student.fatherName && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Father: {collectible.student.fatherName}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography variant="caption" color="text.secondary">Total outstanding</Typography>
                  <Typography variant="h5" fontWeight={800} color={collectible.totalOutstanding > 0 ? 'warning.dark' : 'success.dark'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {collectible.totalOutstanding > 0 ? currencyFmt(collectible.totalOutstanding) : 'Clear'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {collectible.invoices.length > 0
                      ? `across ${collectible.invoices.length} challan${collectible.invoices.length === 1 ? '' : 's'}`
                      : 'nothing to pay'}
                    {advanceOnHand > 0 && ` · ${currencyFmt(advanceOnHand)} held as advance`}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {!result && collectible.invoices.length === 0 && !receivingAdvance && (
              <Alert
                severity="success"
                variant="outlined"
                action={<Button size="small" onClick={() => { setReceivingAdvance(true); setAmountInput(''); }}>Receive an advance</Button>}
              >
                No outstanding challans{advanceOnHand > 0 ? ` · ${currencyFmt(advanceOnHand)} held as advance` : ''}.
              </Alert>
            )}

            {!result && (collectible.invoices.length > 0 || receivingAdvance) && (
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                {/* Left: allocations */}
                {collectible.invoices.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 2, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                      Allocations
                    </Typography>
                    <Tooltip title="Match received amount to full outstanding across every challan">
                      <Button
                        size="small"
                        startIcon={<AutoFixHigh fontSize="small" />}
                        onClick={collectAll}
                      >
                        Collect all outstanding
                      </Button>
                    </Tooltip>
                  </Stack>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell>Challan</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                        <TableCell align="right" sx={{ pr: 0 }}>Allocate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {collectible.invoices.map(inv => {
                        const draft = allocations.find(a => a.invoiceId === inv._id);
                        if (!draft) return null;
                        const hint = invoiceHint(inv);
                        return (
                          <TableRow key={inv._id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={draft.selected}
                                onChange={e => setAllocationField(inv._id, {
                                  selected: e.target.checked,
                                  amount: e.target.checked ? String(inv.totals.outstanding) : '0',
                                })}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                                <ChallanNumberCell value={inv.invoiceNumber} />
                                {hint && (
                                  <Chip
                                    size="small"
                                    label={hint.label}
                                    variant="outlined"
                                    sx={{
                                      height: 18, fontSize: '0.65rem',
                                      borderColor: alpha(hint.color.main, 0.35),
                                      color: hint.color.dark,
                                      bgcolor: alpha(hint.color.main, 0.08),
                                    }}
                                  />
                                )}
                              </Stack>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Due {format(new Date(inv.dueDate), 'MMM d, yyyy')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CalendarMonth fontSize="inherit" sx={{ color: 'text.disabled' }} />
                                <Typography variant="body2">{inv.period.label}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {currencyFmt(inv.totals.outstanding)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ pr: 0 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={draft.amount}
                                onChange={e => setAllocationField(inv._id, { amount: e.target.value, selected: true })}
                                inputProps={{
                                  min: 0,
                                  max: inv.totals.outstanding,
                                  step: 100,
                                  style: { textAlign: 'right' },
                                }}
                                sx={{ width: 140 }}
                                disabled={!draft.selected}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <Divider sx={{ mt: 1.5, mb: 1 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Allocated total</Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={isAllocatedMatch ? 'success.dark' : overAllocated ? 'error.main' : 'warning.dark'}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {currencyFmt(allocatedTotal)}
                    </Typography>
                  </Stack>
                  {useAdvance && fromAdvance > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {currencyFmt(fromAdvance)} paid from advance{amount > 0 ? `, ${currencyFmt(amount)} received now` : ''}.
                    </Typography>
                  )}
                  {overAllocated && (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                      Reduce line amounts by {currencyFmt(allocatedTotal - amount - available)}.
                    </Typography>
                  )}
                  {!useAdvance && extra > 0.005 && (
                    <FormControlLabel
                      sx={{ mt: 0.5, ml: 0 }}
                      control={<Switch size="small" checked={keepExtra} onChange={e => setKeepExtra(e.target.checked)} />}
                      label={<Typography variant="caption">Keep the extra {currencyFmt(extra)} as advance</Typography>}
                    />
                  )}
                </Paper>
                )}

                {/* Right: received details */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 280 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 1 }}>
                    Received
                  </Typography>
                  <Stack spacing={2}>
                    {advanceOnHand > 0 && collectible.invoices.length > 0 && (
                      <Box sx={{ p: 1.25, borderRadius: 1.5, border: 1, borderColor: alpha(theme.palette.success.main, 0.3), bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={<Switch size="small" checked={useAdvance} onChange={e => toggleAdvance(e.target.checked)} />}
                          label={<Typography variant="body2"><strong>{currencyFmt(advanceOnHand)}</strong> held as advance — use it</Typography>}
                        />
                      </Box>
                    )}
                    <TextField
                      label={useAdvance ? 'Received now (beyond the advance)' : receivingAdvance ? 'Advance received' : 'Amount received'}
                      type="number"
                      value={amountInput}
                      onChange={e => handleAmountChange(e.target.value)}
                      inputProps={{ min: 0, step: 100 }}
                      InputProps={{
                        startAdornment: currency ? (
                          <InputAdornment position="start">{currency}</InputAdornment>
                        ) : undefined,
                      }}
                      autoFocus
                    />
                    <FormControl size="small">
                      <InputLabel>Method</InputLabel>
                      <Select
                        label="Method"
                        value={method}
                        onChange={e => setMethod(e.target.value as FeePaymentMethod)}
                      >
                        {PAYMENT_METHODS.map(m => (
                          <MenuItem key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label={
                        method === 'cheque' ? 'Cheque number'
                        : method === 'bank_transfer' ? 'Bank transaction id'
                        : method === 'online' ? 'Gateway reference'
                        : 'Reference (optional)'
                      }
                      value={referenceNumber}
                      onChange={e => setReferenceNumber(e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Received at"
                      type="datetime-local"
                      value={receivedAt}
                      onChange={e => setReceivedAt(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Divider />
                    <FormControl size="small">
                      <InputLabel>Paid by</InputLabel>
                      <Select
                        label="Paid by"
                        value={paidByKind}
                        onChange={e => setPaidByKind(e.target.value as PaidByKind)}
                      >
                        <MenuItem value="parent">Parent</MenuItem>
                        <MenuItem value="guardian">Guardian</MenuItem>
                        <MenuItem value="student">Student</MenuItem>
                        <MenuItem value="applicant">Applicant</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Payer name"
                      value={paidByName}
                      onChange={e => setPaidByName(e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Notes (optional)"
                      multiline
                      minRows={2}
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                    />
                  </Stack>
                </Paper>
              </Stack>
            )}

            {result && (
              <Alert
                severity="success"
                variant="outlined"
                icon={<CheckCircle />}
                action={
                  <Button
                    size="small"
                    startIcon={<Print />}
                    disabled={preparingReceipt}
                    onClick={() => downloadReceipt({ _id: result.payment._id, receiptNumber: result.receiptNumber })}
                  >
                    {preparingReceipt ? 'Preparing…' : 'Receipt'}
                  </Button>
                }
              >
                {resultSummary}{' '}
                Receipt <strong>{result.receiptNumber}</strong>.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            disableElevation
            onClick={save}
            disabled={
              saving
              || !collectible
              || (collectible.invoices.length === 0 && !receivingAdvance)
              || !isAllocatedMatch
              || (nonEmptyAllocations.length === 0 && !advanceAmount)
              || amount + fromAdvance <= 0
            }
          >
            {saving
              ? 'Saving…'
              : amount > 0
                ? `Record ${currencyFmt(amount)}`
                : fromAdvance > 0 ? 'Pay from advance' : 'Record payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RecordFeePaymentDialog;
